import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { layoutStore, useLayoutStore } from "@/stores/layout-store";

import {
  NAVIGATOR_CLOSE_DRAWER_LABEL,
  NAVIGATOR_COLLAPSE_LABEL,
  NAVIGATOR_EXPAND_LABEL,
} from "./navigator.constants";
import type {
  NavigatorEntry,
  NavigatorItem,
  NavigatorPanelState,
  NavigatorProps,
} from "./types-navigator";

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusablesIn(container: HTMLElement | null): HTMLElement[] {
  return container ? Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];
}

function isActive(item: NavigatorItem, pathname: string): boolean {
  return matchPath({ path: item.path, end: item.end ?? true }, pathname) !== null;
}

function toEntries(items: NavigatorItem[], pathname: string, collapsed: boolean): NavigatorEntry[] {
  return items.map((item) => {
    const active = isActive(item, pathname);
    return {
      ...item,
      active,
      ariaCurrent: active ? "page" : undefined,
      tooltip: collapsed ? item.label : undefined,
    };
  });
}

/**
 * ViewModel do Navigator: recolher/expandir (persistido no `layoutStore`), a gaveta do mobile (abre pelo hambúrguer;
 * fecha pelo "<", pela máscara, pelo Esc do `Sidebar` e ao navegar; foco no primeiro item, preso na gaveta e devolvido ao
 * hambúrguer) e o item ativo pela rota. Sem JSX.
 */
export function useNavigator({ items, secondaryItems = [] }: NavigatorProps) {
  const { pathname } = useLocation();
  const { menuIsOpen } = useLayoutStore();
  const collapsed = !menuIsOpen;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hamburgerId = useId();
  const drawerRef = useRef<HTMLElement>(null);
  const wasDrawerOpen = useRef(false);

  const toggleCollapsed = useCallback(
    () => layoutStore.set({ menuIsOpen: collapsed }),
    [collapsed],
  );
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Navegar fecha a gaveta (o clique no item também fecha, para a rota atual).
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // Fechar por qualquer caminho devolve o foco ao hambúrguer.
  useEffect(() => {
    if (wasDrawerOpen.current && !drawerOpen) document.getElementById(hamburgerId)?.focus();
    wasDrawerOpen.current = drawerOpen;
  }, [drawerOpen, hamburgerId]);

  // Chamado pelo `onShow` do `Sidebar`, quando a gaveta termina de entrar.
  const focusDrawer = useCallback(() => {
    focusablesIn(drawerRef.current)[0]?.focus();
  }, []);

  const trapDrawerFocus = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Tab") return;

    const focusables = focusablesIn(drawerRef.current);
    const first = focusables[0];
    const last = focusables.at(-1);
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  const desktop: NavigatorPanelState = {
    collapsed,
    state: collapsed ? "collapsed" : "expanded",
    testId: "navigator",
    toggleTestId: "navigator-toggle",
    userTestId: "navigator-user",
    userVariant: collapsed ? "avatar" : "full",
    toggleLabel: collapsed ? NAVIGATOR_EXPAND_LABEL : NAVIGATOR_COLLAPSE_LABEL,
    toggleIcon: collapsed ? "chevron-right" : "chevron-left",
    toggleExpanded: !collapsed,
    onToggle: toggleCollapsed,
    items: toEntries(items, pathname, collapsed),
    secondaryItems: toEntries(secondaryItems, pathname, collapsed),
    hasSecondary: secondaryItems.length > 0,
  };

  const drawer: NavigatorPanelState = {
    collapsed: false,
    state: "expanded",
    testId: "navigator-drawer",
    toggleTestId: "navigator-drawer-close",
    userTestId: "navigator-drawer-user",
    userVariant: "full",
    toggleLabel: NAVIGATOR_CLOSE_DRAWER_LABEL,
    toggleIcon: "chevron-left",
    toggleExpanded: true,
    onToggle: closeDrawer,
    ref: drawerRef,
    onKeyDown: trapDrawerFocus,
    items: toEntries(items, pathname, false),
    secondaryItems: toEntries(secondaryItems, pathname, false),
    hasSecondary: secondaryItems.length > 0,
  };

  return {
    collapsed,
    drawerOpen,
    toggleCollapsed,
    openDrawer,
    closeDrawer,
    hamburgerId,
    focusDrawer,
    desktop,
    drawer,
  };
}
